import { match, partial } from 'variant';
import { useBiLogger } from '@wix/cli-telemetry-react';
import { useAsyncCallback } from '@wix/cli-ui-kit';
import { useDevCenterClient } from '@wix/dev-center-client';
import { useBackendAsAServiceClient } from '@wix/backend-as-a-service-client';
import type { TemplateSource } from '../../parse-command-options.js';
import type { McpConfigResult } from '../mcp/getMcpResult.js';
import { createAppProjectAndEnvironment } from '../../services/project-setup.js';

export interface DataForAppCreation {
  appName: string;
  packageName: string;
  template: TemplateSource;
  mcpConfigResult: McpConfigResult;
}

export const useAppCreation = ({ extendAppId }: { extendAppId?: string }) => {
  const devCenterClient = useDevCenterClient();
  const backendAsAServiceClient = useBackendAsAServiceClient();
  const biLogger = useBiLogger();
  const createAppCallback = useAsyncCallback(
    async (
      _,
      { appName, packageName, template, mcpConfigResult }: DataForAppCreation
    ) => {
      const { id: appId } = await match(
        template,
        partial({
          App: ({ payload }) =>
            devCenterClient.createAppFromTemplate(
              {
                name: appName,
                templateId: payload.id,
              },
              payload
            ),
          default: () => {
            if (extendAppId) {
              return { id: extendAppId };
            }

            return devCenterClient.createApp({ name: appName });
          },
        })
      );

      const environmentVariables = await createAppProjectAndEnvironment({
        appId,
        appName,
        devCenterClient,
        backendAsAServiceClient,
      });

      biLogger.updateDefaults({
        _appId: appId,
      });

      return {
        template,
        appData: { appName, appId },
        packageName,
        mcpConfigResult,
        environmentVariables,
      };
    }
  );

  return createAppCallback;
};
